import React, { useEffect, useMemo, useState } from 'react';
import { API_BASE_URL } from '../config/apiConfig';

function formatTime(value) {
  if (!value) {
    return '';
  }

  return new Intl.DateTimeFormat('es-UY', {
    dateStyle: 'short',
    timeStyle: 'short'
  }).format(new Date(value));
}

function ChatAdmin() {
  const [token, setToken] = useState(() => window.localStorage.getItem('babyplanner_admin_token') || '');
  const [tokenInput, setTokenInput] = useState(token);
  const [chats, setChats] = useState([]);
  const [selectedChatId, setSelectedChatId] = useState('');
  const [selectedChat, setSelectedChat] = useState(null);
  const [reply, setReply] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [adminName, setAdminName] = useState('Admin');

  const authHeaders = useMemo(() => ({
    Authorization: `Bearer ${token}`
  }), [token]);

  const selectedSummary = chats.find((chat) => chat.id === selectedChatId);

  const loadChats = async () => {
    if (!token) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/chats`, {
        headers: authHeaders
      });
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'No se pudieron cargar los chats.');
      }

      setChats(data.chats || []);
      setAdminName(data.adminName || 'Admin');
      setError('');

      if (!selectedChatId && data.chats?.[0]) {
        setSelectedChatId(data.chats[0].id);
      }
    } catch (loadError) {
      setError(loadError.message);
    }
  };

  const loadSelectedChat = async () => {
    if (!token || !selectedChatId) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/chats/${selectedChatId}`, {
        headers: authHeaders
      });
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'No se pudo cargar la conversación.');
      }

      setSelectedChat(data.chat);
      setAdminName(data.chat?.adminName || adminName);
      setError('');
    } catch (loadError) {
      setError(loadError.message);
    }
  };

  useEffect(() => {
    loadChats();
    const interval = window.setInterval(loadChats, 4000);

    return () => window.clearInterval(interval);
  }, [token, selectedChatId]);

  useEffect(() => {
    loadSelectedChat();
    const interval = window.setInterval(loadSelectedChat, 2500);

    return () => window.clearInterval(interval);
  }, [token, selectedChatId]);

  const handleLogin = (event) => {
    event.preventDefault();
    const trimmed = tokenInput.trim();
    setToken(trimmed);
    window.localStorage.setItem('babyplanner_admin_token', trimmed);
  };

  const handleLogout = () => {
    setToken('');
    setTokenInput('');
    setChats([]);
    setSelectedChat(null);
    setSelectedChatId('');
    window.localStorage.removeItem('babyplanner_admin_token');
  };

  const sendReply = async (event) => {
    event.preventDefault();
    const trimmed = reply.trim();

    if (!trimmed || !selectedChatId) {
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/chats/${selectedChatId}/reply`, {
        method: 'POST',
        headers: {
          ...authHeaders,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ message: trimmed })
      });
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'No se pudo responder.');
      }

      setSelectedChat(data.chat);
      setReply('');
      loadChats();
      setError('');
    } catch (sendError) {
      setError(sendError.message);
    } finally {
      setIsLoading(false);
    }
  };

  const setAdminMode = async (adminActive) => {
    if (!selectedChatId) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/chats/${selectedChatId}/mode`, {
        method: 'POST',
        headers: {
          ...authHeaders,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ adminActive })
      });
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'No se pudo cambiar el modo.');
      }

      setSelectedChat(data.chat);
      loadChats();
      setError('');
    } catch (modeError) {
      setError(modeError.message);
    }
  };

  if (!token) {
    return (
      <main className="page-shell admin-page">
        <section className="admin-login-card">
          <p className="eyebrow">Chat admin</p>
          <h2>Ingresar al panel</h2>
          <p className="section-lede">Usa el token definido en <code>ADMIN_CHAT_TOKEN</code> en el env del backend.</p>
          <form className="form-grid" onSubmit={handleLogin}>
            <div className="form-field">
              <label htmlFor="admin-token">Token</label>
              <input
                id="admin-token"
                type="password"
                value={tokenInput}
                onChange={(event) => setTokenInput(event.target.value)}
                autoComplete="current-password"
                required
              />
            </div>
            <button type="submit" className="button button-primary">Entrar</button>
          </form>
        </section>
      </main>
    );
  }

  return (
    <main className="page-shell admin-page">
      <section className="admin-shell">
        <header className="admin-topbar">
          <div>
            <p className="eyebrow">Chat admin</p>
            <h2>Conversaciones</h2>
          </div>
          <button type="button" className="button button-secondary" onClick={handleLogout}>Salir</button>
        </header>

        {error && <p className="admin-error">{error}</p>}

        <div className="admin-layout">
          <aside className="admin-chat-list" aria-label="Lista de chats">
            {chats.length === 0 && <p className="admin-empty">Todavía no hay chats.</p>}
            {chats.map((chat) => (
              <button
                type="button"
                className={`admin-chat-item ${chat.id === selectedChatId ? 'is-selected' : ''}`}
                key={chat.id}
                onClick={() => setSelectedChatId(chat.id)}
              >
                <strong>{chat.id.slice(0, 18)}</strong>
                <span>{chat.lastMessage?.text || 'Sin mensajes'}</span>
                <small>{formatTime(chat.updatedAt)}</small>
                {chat.unreadUserMessages > 0 && <em>{chat.unreadUserMessages}</em>}
              </button>
            ))}
          </aside>

          <section className="admin-chat-detail">
            {!selectedChat && <p className="admin-empty">Selecciona un chat para verlo.</p>}
            {selectedChat && (
              <>
                <div className="admin-chat-toolbar">
                  <div>
                    <strong>{selectedSummary?.id || selectedChat.id}</strong>
                    <span>{selectedChat.adminActive ? 'Modo admin activo' : 'Responde el LLM'}</span>
                  </div>
                  <button
                    type="button"
                    className="button button-secondary"
                    onClick={() => setAdminMode(!selectedChat.adminActive)}
                  >
                    {selectedChat.adminActive ? 'Devolver al LLM' : 'Tomar chat'}
                  </button>
                </div>

                <div className="admin-messages">
                  {selectedChat.messages.map((message) => (
                    <div className={`admin-message ${message.sender}`} key={message.id}>
                      <span>{message.sender === 'user' ? 'Usuario' : message.sender === 'admin' ? (message.displayName || adminName) : 'LLM'}</span>
                      <p>{message.text}</p>
                      <small>{formatTime(message.createdAt)}</small>
                    </div>
                  ))}
                </div>

                <form className="admin-reply-form" onSubmit={sendReply}>
                  <textarea
                    value={reply}
                    onChange={(event) => setReply(event.target.value)}
                    placeholder="Escribe una respuesta como admin..."
                    rows="4"
                  />
                  <button type="submit" className="button button-primary" disabled={isLoading || !reply.trim()}>
                    {isLoading ? 'Enviando...' : 'Responder como ' + adminName}
                  </button>
                </form>
              </>
            )}
          </section>
        </div>
      </section>
    </main>
  );
}

export default ChatAdmin;
